/**
 * The pure half of the OLT onboarding wizard: the generated credentials, and
 * the VLAN rows as the page edits them.
 */
import {
  OLT_SECRET_BYTES,
  OLT_VLAN_LABEL_PATTERN,
  oltCommunityFromBytes,
  oltPasswordFromBytes,
  type OnboardingVlanInput,
} from '@simpleacs/shared';

const randomBytes = (): Uint8Array => {
  const bytes = new Uint8Array(OLT_SECRET_BYTES);
  crypto.getRandomValues(bytes);
  return bytes;
};

/**
 * A fresh CLI password — the operator types it into the chassis's user block,
 * so it is shown, not masked (operator, 2026-09-25: "autogenerate simpleacs
 * password").
 */
export function generateOltPassword(): string {
  return oltPasswordFromBytes(randomBytes());
}

/**
 * A fresh community — never the old fixed `simpleacs-ro`, and read-write as
 * well as read, because the platform writes over SNMP now (operator,
 * 2026-09-25: "set autogenerate community strings", "set snmp read/write").
 */
export function generateOltCommunity(kind: 'ro' | 'rw'): string {
  return oltCommunityFromBytes(kind, randomBytes());
}

/** One VLAN row as typed — strings, because an input holds a half-typed number. */
export interface VlanDraft {
  /** Stable React key; not sent. */
  key: string;
  vlanId: string;
  name: string;
  management: boolean;
  subnet: string;
}

let draftCounter = 0;
export function newVlanDraft(): VlanDraft {
  draftCounter += 1;
  return { key: `vlan-${draftCounter}`, vlanId: '', name: '', management: false, subnet: '' };
}

const CIDR = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d|[12]\d|3[0-2])$/;

function validCidr(value: string): boolean {
  const match = CIDR.exec(value.trim());
  return Boolean(match && match.slice(1, 5).every((octet) => Number(octet) <= 255));
}

/**
 * What is wrong with each row, keyed by the row's `key`, in the words the
 * row shows under itself. An empty row (nothing typed) is ignored, not
 * refused — it is where the operator stopped adding.
 */
export function vlanDraftProblems(rows: VlanDraft[]): Map<string, string> {
  const problems = new Map<string, string>();
  const seen = new Map<number, string>();
  for (const row of rows) {
    if (isEmptyDraft(row)) continue;
    const id = Number(row.vlanId.trim());
    if (!/^\d+$/.test(row.vlanId.trim()) || id < 1 || id > 4094) {
      problems.set(row.key, 'A VLAN id is a whole number from 1 to 4094.');
      continue;
    }
    if (seen.has(id)) {
      problems.set(row.key, `VLAN ${id} is already listed.`);
      continue;
    }
    seen.set(id, row.key);
    const name = row.name.trim();
    if (name && !OLT_VLAN_LABEL_PATTERN.test(name)) {
      problems.set(row.key, 'A name takes letters, digits, spaces, dot, dash and underscore only.');
      continue;
    }
    if (row.management && !validCidr(row.subnet)) {
      problems.set(
        row.key,
        row.subnet.trim()
          ? `“${row.subnet.trim()}” is not a subnet — write it like 10.10.0.0/16.`
          : `A management VLAN needs the subnet its ONUs take their addresses from.`,
      );
    }
  }
  return problems;
}

export function isEmptyDraft(row: VlanDraft): boolean {
  return !row.vlanId.trim() && !row.name.trim() && !row.subnet.trim() && !row.management;
}

/**
 * The rows as the API takes them: empty rows dropped, names trimmed, and a
 * subnet only on a management VLAN — a VLAN without the role does not need
 * one (operator, 2026-09-25).
 */
export function toOnboardingVlans(rows: VlanDraft[]): OnboardingVlanInput[] {
  return rows
    .filter((row) => !isEmptyDraft(row))
    .map((row) => ({
      vlanId: Number(row.vlanId.trim()),
      ...(row.name.trim() ? { name: row.name.trim() } : {}),
      management: row.management,
      ...(row.management ? { subnet: row.subnet.trim() } : {}),
    }));
}
